import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Modal,
  StyleProp,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {useTheme} from 'contexts/ThemeContext';
import LongRectangleTextButton from 'components/atoms/longRectangleTextButton';
import strings from '../../../translations/en';
import constants from '../../../constants';
import CalendarDatePickerView from 'components/atoms/calendarDatePickerView';
import CalendarArrowsView from 'components/atoms/calendarArrowsView';
import {YEARS} from 'constants/calendarConstants';
import PickerView from '../pickerView';
import styleSheet from './styles';
import CalendarView from '../calendarView';
import {isFirstDateBigger} from 'helpers/compareDates';

const MIDDLE_DAY_OF_MONTH = 15;

export interface CalendarModalHandle {
  switchVisible: () => void;
}

type CalendarProps = {
  onPress: (day: string) => void;
  modalWindowStyle: StyleProp<any>;
  buttonLabel: string;
  minDate?: Date;
  selected?: string;
};

const CalendarModal = React.forwardRef<CalendarModalHandle, CalendarProps>(
  (props, ref) => {
    const theme = useTheme();
    const styles = styleSheet(theme);
    const {CALENDAR} = constants;
    const [visible, setVisible] = useState(false);

    const [isCalendar, setIsCalendar] = useState(true);

    const minDateString = props.minDate?.toString();
    const calendarMomentFormat = moment().format(CALENDAR.momentFormat);
    const [initialDate, setInitialDate] = useState(
      minDateString ? minDateString : calendarMomentFormat,
    );

    const selectedCalculateValue = minDateString
      ? minDateString
      : calendarMomentFormat;

    const [selected, setSelected] = useState(
      props.selected ? props.selected : selectedCalculateValue,
    );

    const [pickerMonth, setPickerMonth] = useState(
      moment(initialDate).format(CALENDAR.monthPickerFormat),
    );
    const [pickerYear, setPickerYear] = useState(
      moment(initialDate).format(CALENDAR.yearPickerFormat),
    );

    useImperativeHandle(ref, () => ({
      switchVisible() {
        setVisible(!visible);
      },
    }));

    useEffect(() => {
      if (
        props.minDate &&
        isFirstDateBigger(props.minDate, new Date(selected))
      ) {
        setSelected('');
      }
    }, [props.minDate]);

    useEffect(() => {
      if (!props.selected) {
        return;
      }

      setSelected(props.selected);
      setInitialDate(props.selected);
    }, [props.selected]);

    useEffect(() => {
      const date = new Date(initialDate);
      if (date.getDate() == MIDDLE_DAY_OF_MONTH) {
        return;
      }

      date.setDate(MIDDLE_DAY_OF_MONTH);
      setInitialDate(moment(date).format(CALENDAR.momentFormat));
    }, [initialDate]);

    const onDayPress = (date: string) => {
      setSelected(date);
      setInitialDate(date);
    };

    const markedDates = useMemo(() => {
      const selectedFormattedDate = moment(selected).format(
        CALENDAR.momentFormat,
      );

      return {
        [selectedFormattedDate]: {
          selected: true,
          disableTouchEvent: true,
          selectedColor: theme.colors.cornflowerBlueWithOpacity(0.13),
          selectedTextColor: theme.colors.cornflowerBlue,
        },
      };
    }, [selected]);

    const handleFirstPickerChange = (month: string) => {
      setPickerMonth(month);
    };

    const handleSecondPickerChange = (year: string) => {
      setPickerYear(year);
    };

    const onPressOut = () => {
      if (props.selected) {
        setSelected(props.selected);
        setInitialDate(props.selected);
      }

      setIsCalendar(true);
      setVisible(false);
    };

    const onPressJumpToDate = () => {
      if (isCalendar) {
        props.onPress(selected);
        setVisible(!visible);
      } else {
        let date = `${pickerYear}-${moment()
          .month(pickerMonth)
          .format(CALENDAR.convertMonthToCalendarFormat)}-${moment(
          initialDate,
        ).format('DD')}`;

        if (
          moment(initialDate).format(CALENDAR.monthPickerFormat) !=
          moment(date).format(CALENDAR.monthPickerFormat)
        ) {
          date = `${pickerYear}-${moment()
            .month(pickerMonth)
            .format(CALENDAR.convertMonthToCalendarFormat)}-01`;
        }

        setInitialDate(date);
        setIsCalendar(true);
      }
    };

    const HeaderContainer = (
      <View style={styles.headerContainer}>
        <CalendarDatePickerView
          title={moment(initialDate).format(CALENDAR.calendarFormat)}
          onPress={() => {
            setIsCalendar(!isCalendar);
          }}
        />
        <CalendarArrowsView
          onPressLeft={() => {
            setInitialDate(
              moment(initialDate)
                .subtract(1, 'month')
                .format(CALENDAR.momentFormat),
            );
          }}
          onPressRight={() => {
            setInitialDate(
              moment(initialDate).add(1, 'month').format(CALENDAR.momentFormat),
            );
          }}
        />
      </View>
    );

    const Calendar = (
      <CalendarView
        headerContainer={HeaderContainer}
        initialDate={initialDate}
        minDate={props.minDate?.toString()}
        markedDates={markedDates}
        onDayPress={onDayPress}
      />
    );

    const Picker = (
      <>
        <View style={styles.headerView}>{HeaderContainer}</View>
        <PickerView
          pickerData={strings.calendarTitles.monthsFull}
          secondPickerData={YEARS}
          onChangeValue={handleFirstPickerChange}
          secondPickerOnChangeValue={handleSecondPickerChange}
          isTwoColumnsPicker={true}
          isYearPicker={true}
          selectedValue={pickerMonth}
          secondPickerSelectedValue={pickerYear}
        />
      </>
    );

    const JumpToDateButton = (
      <View style={styles.jumpToDateContainer}>
        <LongRectangleTextButton
          title={props.buttonLabel}
          onPress={onPressJumpToDate}
        />
      </View>
    );

    return (
      <Modal
        animationType="slide"
        onRequestClose={() => {}}
        transparent={true}
        visible={visible}>
        <TouchableOpacity
          style={[styles.defaultContainerStyle, props.modalWindowStyle]}
          activeOpacity={1}
          onPressOut={onPressOut}>
          <TouchableWithoutFeedback>
            <View style={styles.defaultModalWindowStyle}>
              {isCalendar ? Calendar : Picker}
              {JumpToDateButton}
            </View>
          </TouchableWithoutFeedback>
        </TouchableOpacity>
      </Modal>
    );
  },
);

export default CalendarModal;
